import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../services/api/client';
import { ENDPOINTS } from '../services/api/endpoints';

export function useSellers({ platforms = [], notificationTypes = [], search = '', managerName = '', notifEnabledType = '', notifDisabledType = '', sortBy = '', sortDir = '', page = 0, size = 0 } = {}) {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 0, size: 0, totalElements: 0, totalPages: 0 });

  const filterKey = JSON.stringify({
    platforms: [...platforms].sort(),
    notificationTypes: [...notificationTypes].sort(),
    search,
    managerName,
    notifEnabledType,
    notifDisabledType,
    sortBy,
    sortDir,
    page,
    size,
  });

  const requestIdRef = useRef(0);

  const fetchSellers = useCallback(async () => {
    const myRequestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      platforms.forEach(p => { if (p) params.append('platforms', p); });
      notificationTypes.forEach(n => { if (n) params.append('notificationTypes', n); });
      if (search) params.set('search', search);
      if (managerName) params.set('managerName', managerName);
      if (notifEnabledType) params.set('notifEnabled', notifEnabledType);
      if (notifDisabledType) params.set('notifDisabled', notifDisabledType);
      if (sortBy) params.set('sortBy', sortBy);
      if (sortDir) params.set('sortDir', sortDir);
      if (size > 0) {
        params.set('page', page);
        params.set('size', size);
      }
      const qs = params.toString();
      const url = `${ENDPOINTS.SELLERS.GET_ALL}${qs ? `?${qs}` : ''}`;
      const res = await apiClient.get(url);

      if (myRequestId !== requestIdRef.current) return;

      let raw = res?.data ?? res;

      // Paginated response: { content, page, size, totalElements, totalPages }
      if (raw && !Array.isArray(raw) && Array.isArray(raw.content)) {
        const data = dedup(raw.content);
        setSellers(data);
        setPagination({
          page: raw.page ?? 0,
          size: raw.size ?? size,
          totalElements: raw.totalElements ?? data.length,
          totalPages: raw.totalPages ?? 1,
        });
      } else {
        // Non-paginated (backward compatible)
        const data = Array.isArray(raw) ? dedup(raw) : [];
        setSellers(data);
        setPagination({ page: 0, size: 0, totalElements: data.length, totalPages: 1 });
      }
    } catch (err) {
      if (myRequestId !== requestIdRef.current) return;
      setError(err);
      setSellers([]);
    } finally {
      if (myRequestId === requestIdRef.current) setLoading(false);
    }
  }, [filterKey]);

  useEffect(() => { fetchSellers(); }, [fetchSellers]);

  return { sellers, setSellers, loading, error, pagination, refetch: fetchSellers };
}

function dedup(arr) {
  const seen = new Set();
  return arr.filter(s => {
    if (!s.id || seen.has(s.id)) return false;
    seen.add(s.id);
    return true;
  });
}
